import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { MAIN_CONTENT_ID } from "./useRouteFocus";

const HEADING_FOCUS_OPTIONS = { preventScroll: true };

export const focusIsLost = () => {
  const active = document.activeElement;
  return !active || active === document.body || !active.isConnected;
};

const focusElement = (element, options) => {
  if (!element?.isConnected || element.disabled) return false;
  element.focus(options);
  return document.activeElement === element;
};

const mostSpecificPageHeading = () => {
  const headings = document
    .getElementById(MAIN_CONTENT_ID)
    ?.querySelectorAll("[data-page-heading]");
  return headings?.length ? headings[headings.length - 1] : null;
};

export function useLostFocusReturn(containerRef, { keyOf, targetsFor } = {}) {
  const optionsRef = useRef({ keyOf, targetsFor });
  optionsRef.current = { keyOf, targetsFor };
  const lastFocusRef = useRef(null);
  const [request, setRequest] = useState(null);

  const returnFocus = useCallback((key) => {
    const targets = optionsRef.current.targetsFor?.(key) || [];
    if (targets.some((target) => focusElement(target))) return;
    focusElement(mostSpecificPageHeading(), HEADING_FOCUS_OPTIONS);
  }, []);

  useEffect(() => {
    const onFocusIn = (event) => {
      const container = containerRef.current;
      const { target } = event;
      lastFocusRef.current =
        container && container.contains(target)
          ? { element: target, key: optionsRef.current.keyOf?.(target) }
          : null;
    };
    const onFocusOut = (event) => {
      const last = lastFocusRef.current;
      if (event.relatedTarget || last?.element !== event.target) return;
      Promise.resolve().then(() => {
        if (
          lastFocusRef.current === last &&
          last.element.isConnected &&
          document.activeElement !== last.element
        ) {
          lastFocusRef.current = null;
        }
      });
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [containerRef]);

  useLayoutEffect(() => {
    const last = lastFocusRef.current;
    if (!last || last.element.isConnected || !focusIsLost()) return;
    lastFocusRef.current = null;
    returnFocus(last.key);
  });

  useEffect(() => {
    if (request && focusIsLost()) returnFocus(request.key);
  }, [request, returnFocus]);

  return useCallback((key) => setRequest({ key }), []);
}
